import { useForm, Link, router } from '@inertiajs/react'
import { useState, useRef } from 'react'
import Layout from '../Layout'

interface Account {
  id: number
  name: string
  slug: string
  logo_url?: string | null
  carousel_images?: string[]
  shipping_cost_cents: number
  price_input_mode: number
  accept_transfer: boolean
  accept_card: boolean
  loyalty_points_enabled?: boolean
}
interface BankAccount {
  id: number
  bank_name: string
  account_number: string
  account_holder: string
  currency: string
}
interface Props {
  account: Account
  bank_accounts: BankAccount[]
  banks: string[]
  currencies: string[]
  errors?: string[]
  permissions?: { can_manage_account: boolean }
}

interface CarouselImage {
  url: string
  signed_id: string
}

export default function AccountSettings({ account, bank_accounts, banks, currencies, errors, permissions }: Props) {
  const { data, setData, patch, processing } = useForm({
    name: account.name,
    logo: null as File | null,
    shipping_cost_cents: account.shipping_cost_cents ?? 0,
    price_input_mode: account.price_input_mode ?? 0,
    accept_transfer: account.accept_transfer ?? true,
    accept_card: account.accept_card ?? true,
    loyalty_points_enabled: account.loyalty_points_enabled ?? false,
  })

  const bankForm = useForm({
    bank_name: banks[0],
    account_number: '',
    account_holder: '',
    currency: 'LPS',
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(`/${account.slug}/settings/account`, {
      forceFormData: true,
    })
  }

  const handleAddBank = (e: React.FormEvent) => {
    e.preventDefault()
    bankForm.post(`/${account.slug}/settings/bank_accounts`, {
      preserveScroll: true,
      onSuccess: () => bankForm.reset('account_number', 'account_holder'),
    })
  }

  const handleDeleteBank = (id: number) => {
    if (confirm('¿Eliminar esta cuenta bancaria?')) {
      router.delete(`/${account.slug}/settings/bank_accounts/${id}`, {
        preserveScroll: true,
      })
    }
  }

  // ── Carousel ──
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [carouselUploading, setCarouselUploading] = useState(false)
  const [carouselImages, setCarouselImages] = useState<CarouselImage[]>(() => {
    // Convert account.carousel_images URLs to CarouselImage objects
    return (account.carousel_images || []).map((url) => ({
      url,
      signed_id: extractSignedId(url),
    }))
  })

  function extractSignedId(url: string): string {
    // URLs look like: /rails/active_storage/blobs/redirect/:signed_id/:filename
    const parts = url.replace(/\?.*$/, '').split('/')
    return parts[parts.length - 2] || ''
  }

  const handleCarouselUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    setCarouselUploading(true)
    const formData = new FormData()
    Array.from(files).forEach((f) => formData.append('images[]', f))

    try {
      const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
      const res = await fetch(`/${account.slug}/settings/carousel_images`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrf },
        body: formData,
      })
      const json = await res.json()
      if (json.success) {
        // Reload the page to get fresh URLs from backend
        window.location.reload()
      } else {
        alert(json.error || 'Error al subir imágenes')
      }
    } catch {
      alert('Error de conexión')
    } finally {
      setCarouselUploading(false)
    }
  }

  const handleDeleteCarousel = async (signedId: string) => {
    if (!confirm('¿Eliminar esta imagen del carrousel?')) return
    try {
      const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
      const res = await fetch(`/${account.slug}/settings/carousel_images/${encodeURIComponent(signedId)}`, {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': csrf, 'Accept': 'application/json' },
      })
      const json = await res.json()
      if (json.success) {
        setCarouselImages(
          (json.urls as string[]).map((url) => ({ url, signed_id: extractSignedId(url) }))
        )
      } else {
        alert(json.error || 'Error al eliminar')
      }
    } catch {
      alert('Error de conexión')
    }
  }

  return (
    <Layout account={account} permissions={permissions}>
      <div className="max-w-2xl mx-auto space-y-8">
        <h1 className="text-2xl font-bold text-[#202020]">Configuración de la Tienda</h1>

        {errors && errors.length > 0 && (
          <div className="bg-red-50 text-red-600 p-3 rounded">
            {errors.map((error, i) => <p key={i}>{error}</p>)}
          </div>
        )}

        {/* ---- Identidad ---- */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="font-semibold text-[#202020] mb-4">Identidad de la tienda</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de la tienda *</label>
              <input type="text" value={data.name} onChange={(e) => setData('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]"
                required />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Logo de la tienda</label>
              {account.logo_url && (
                <div className="mb-3">
                  <img src={account.logo_url} alt="Logo actual" className="h-20 w-auto border rounded" />
                </div>
              )}
              <input type="file" accept="image/*"
                onChange={(e) => setData('logo', e.target.files?.[0] || null)}
                className="w-full text-sm text-gray-700" />
              <p className="text-xs text-gray-500 mt-1">
                Se usará en el catálogo web, pedidos, facturas POS y otros formatos.
                Formato recomendado: PNG con fondo transparente, 400x400px.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Costo de Envío</label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500 text-sm">L</span>
                <input type="number" step="0.01" min="0"
                  value={(data.shipping_cost_cents / 100).toFixed(2)}
                  onChange={(e) => setData('shipping_cost_cents', Math.round(parseFloat(e.target.value || '0') * 100))}
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]"
                  placeholder="0.00" />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Cargo fijo por envío a domicilio. No incluye IVA. Se muestra al cliente en el checkout.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Modo de ingreso de precios</label>
              <select value={data.price_input_mode.toString()} onChange={(e) => setData('price_input_mode', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]">
                <option value="0">Sin IVA — ingreso precio base (actual)</option>
                <option value="1">Con IVA — ingreso precio final, sistema calcula base</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Controla cómo se ingresan los precios de los productos. En modo "Con IVA", el
                sistema calcula automáticamente el precio base al guardar.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de negocio</label>
              <select disabled
                className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-100 text-gray-500 cursor-not-allowed">
                <option value="0" selected={account.tenant_type === 0 || account.tenant_type === 'tienda'}>Tienda de Productos Varios</option>
                <option value="1" selected={account.tenant_type === 1 || account.tenant_type === 'restaurante'}>Restaurante/Food Truck/CoffeeShop</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                El tipo de negocio se define al crear la cuenta y no puede modificarse.
              </p>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Link href={`/${account.slug}/dashboard`}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50">Cancelar</Link>
              <button type="submit" disabled={processing}
                className="bg-[#FFC107] text-[#202020] px-4 py-2 rounded font-semibold hover:bg-[#e6ad06] disabled:opacity-50">
                {processing ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            </div>
          </form>
        </div>

        {/* ---- Carrousel (panel independiente) ---- */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="font-semibold text-[#202020] mb-4">Imágenes del carrousel</h2>
          <p className="text-sm text-gray-500 mb-4">
            Aparecen en el catálogo público como slider debajo del logo. Recomendado: imágenes horizontales (1200×400px).
          </p>

          {carouselImages.length > 0 && (
            <div className="grid grid-cols-3 md:grid-cols-4 gap-3 mb-4">
              {carouselImages.map((img, i) => (
                <div key={i} className="relative border rounded-lg overflow-hidden bg-gray-100">
                  <img src={img.url} alt={`Carrousel ${i + 1}`} className="w-full h-24 object-cover" />
                  <button
                    onClick={() => handleDeleteCarousel(img.signed_id)}
                    className="absolute top-1 right-1 bg-red-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                    title="Eliminar imagen"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              disabled={carouselUploading}
              onChange={handleCarouselUpload}
              className="hidden"
              id="carousel-file-input"
            />
            <label
              htmlFor="carousel-file-input"
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
                carouselUploading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-[#55ACEE] text-white hover:bg-blue-600'
              }`}
            >
              {carouselUploading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Subiendo...
                </>
              ) : (
                <>📷 Seleccionar imágenes</>
              )}
            </label>
          </div>
          {carouselImages.length > 0 && (
            <p className="text-xs text-gray-400 mt-2">{carouselImages.length} imagen(es) en el carrousel</p>
          )}
        </div>

        {/* ---- Cuentas Bancarias ---- */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="font-semibold text-[#202020] mb-4">Cuentas bancarias para transferencias</h2>

          <form onSubmit={handleAddBank} className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Banco</label>
              <select value={bankForm.data.bank_name} onChange={(e) => bankForm.setData('bank_name', e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#55ACEE]">
                {banks.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">N° de cuenta *</label>
              <input type="text" value={bankForm.data.account_number}
                onChange={(e) => bankForm.setData('account_number', e.target.value)}
                placeholder="000-000-000"
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#55ACEE]"
                required />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Titular *</label>
              <input type="text" value={bankForm.data.account_holder}
                onChange={(e) => bankForm.setData('account_holder', e.target.value)}
                placeholder="Nombre del titular"
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#55ACEE]"
                required />
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">Moneda</label>
                <select value={bankForm.data.currency} onChange={(e) => bankForm.setData('currency', e.target.value)}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#55ACEE]">
                  {currencies.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <button type="submit" disabled={bankForm.processing}
                className="bg-[#55ACEE] text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-[#3a8bc4] disabled:opacity-50 whitespace-nowrap">
                {bankForm.processing ? '...' : 'Agregar'}
              </button>
            </div>
          </form>

          {bank_accounts.length === 0 ? (
            <p className="text-sm text-gray-500">No hay cuentas bancarias registradas.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-gray-500">
                  <th className="pb-2 font-medium">Banco</th>
                  <th className="pb-2 font-medium">Cuenta</th>
                  <th className="pb-2 font-medium">Titular</th>
                  <th className="pb-2 font-medium">Moneda</th>
                  <th className="pb-2"></th>
                </tr>
              </thead>
              <tbody>
                {bank_accounts.map(ba => (
                  <tr key={ba.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="py-2 font-medium text-[#202020]">{ba.bank_name}</td>
                    <td className="py-2 text-gray-700">{ba.account_number}</td>
                    <td className="py-2 text-gray-700">{ba.account_holder}</td>
                    <td className="py-2">
                      <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${ba.currency === 'USD' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                        {ba.currency}
                      </span>
                    </td>
                    <td className="py-2 text-right">
                      <button onClick={() => handleDeleteBank(ba.id)}
                        className="text-red-500 hover:text-red-700 text-xs font-medium">
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ---- Métodos de Pago ---- */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="font-semibold text-[#202020] mb-4">Métodos de pago en tu tienda en línea</h2>
          <div className="space-y-4">
            <label className="flex items-start gap-3 p-3 border rounded cursor-pointer hover:bg-gray-50">
              <input type="checkbox"
                checked={data.accept_transfer}
                onChange={(e) => setData('accept_transfer', e.target.checked)}
                className="mt-1" />
              <div>
                <p className="font-medium text-[#202020]">Transferencia bancaria</p>
                <p className="text-sm text-gray-500">El cliente paga mediante transferencia y sube su comprobante.</p>
                {!data.accept_transfer && (
                  <p className="text-xs text-red-500 mt-1">⚠ Requiere al menos una cuenta bancaria</p>
                )}
              </div>
            </label>
            <label className="flex items-start gap-3 p-3 border rounded cursor-pointer hover:bg-gray-50">
              <input type="checkbox"
                checked={data.accept_card}
                onChange={(e) => setData('accept_card', e.target.checked)}
                className="mt-1" />
              <div>
                <p className="font-medium text-[#202020]">Pago con tarjeta</p>
                <p className="text-sm text-gray-500">El cliente paga con tarjeta de crédito o débito mediante un link de pago.</p>
                {!data.accept_card && (
                  <p className="text-xs text-amber-600 mt-1">⚠ Requiere API key de BAC (pendiente)</p>
                )}
              </div>
            </label>
          </div>
          <p className="text-xs text-gray-400 mt-4">
            Los métodos desactivados no se mostrarán a los clientes durante el checkout.
          </p>
        </div>

        {/* ---- Programa de Puntos ---- */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="font-semibold text-[#202020] mb-4">Programa de puntos</h2>
          <label className="flex items-start gap-3 p-3 border rounded cursor-pointer hover:bg-gray-50">
            <input type="checkbox"
              checked={data.loyalty_points_enabled}
              onChange={(e) => setData('loyalty_points_enabled', e.target.checked)}
              className="mt-1" />
            <div>
              <p className="font-medium text-[#202020]">Dar puntos a mis clientes</p>
              <p className="text-sm text-gray-500">
                Activa el concepto de puntos de fidelidad en el catálogo (checkout y portal del cliente). El sistema de puntos se habilitará próximamente.
              </p>
            </div>
          </label>
        </div>
      </div>
    </Layout>
  )
}
